import type { FormationSlot, MonsterEntity } from "../types/entity.js";
import { randomInt } from "../combat/dice.js";

/**
 * Target-selection rules for enemy attacks.
 *
 * Implements the table in `specs/enemies-and-ai.md §Target selection`:
 *  - melee-reach-1 → must be adjacent, hits front row only
 *  - melee-reach-2 → adjacent, can reach back row
 *  - ranged-single → 80% front / 20% back by default; overridable per enemy
 *  - aoe          → all four party slots (no friendly-fire, per combat-physical)
 *  - spell-single → weighted to front unless the spell prefers back row
 *
 * Kept as a pure function with an injectable RNG so the selection rules can be
 * tested deterministically without spinning up CombatManager or PartyStore.
 */

export type AttackShape =
  | "melee-reach-1"
  | "melee-reach-2"
  | "ranged-single"
  | "aoe"
  | "spell-single";

export interface EnemyAttackProfile {
  readonly shape: AttackShape;
  /**
   * For `ranged-single`: chance (0..1) the target is a back-row member.
   * Default is 0.2 per spec (80% front / 20% back). Dark-archer-style enemies
   * set this to 0.5 to even the odds.
   */
  readonly rangedBackChance?: number;
  /**
   * For `spell-single`: when true, invert the default weighting to prefer back
   * row (e.g. Hold Person hunting enemy casters). Default false (front-weighted).
   */
  readonly spellPrefersBack?: boolean;
}

/** The minimal party-member shape the selector needs. */
export interface TargetingMember {
  readonly instanceId: string;
  readonly hp: number;
  /** null when the party has no assigned formation (v1 single-member MVP). */
  readonly formationSlot: FormationSlot | null;
}

/** Default chance a ranged attack targets the back row. */
export const DEFAULT_RANGED_BACK_CHANCE = 0.2;
/** Default chance a back-preferring spell still lands on the front row. */
export const SPELL_OFF_ROW_CHANCE = 0.2;

type Rng = (min: number, max: number) => number;

// ── Public API ───────────────────────────────────────────────────────────────

/**
 * Resolve the list of party members an enemy attack should affect.
 * Returns instanceIds so the caller can apply damage in whatever order it likes.
 *
 * Returns an empty array when no valid target exists (all members dead).
 */
export function selectAttackTargets(
  profile: EnemyAttackProfile,
  members: readonly TargetingMember[],
  rng: Rng = randomInt,
): readonly string[] {
  const alive = members.filter((m) => m.hp > 0);
  if (alive.length === 0) return [];

  switch (profile.shape) {
    case "aoe":
      return alive.map((m) => m.instanceId);

    case "melee-reach-1": {
      const front = frontRow(alive);
      const pool = front.length > 0 ? front : alive;
      return [pickUniform(pool, rng).instanceId];
    }

    case "melee-reach-2":
      return [pickUniform(alive, rng).instanceId];

    case "ranged-single": {
      const backChance = clamp01(profile.rangedBackChance ?? DEFAULT_RANGED_BACK_CHANCE);
      return [pickWeightedRow(alive, backChance, rng).instanceId];
    }

    case "spell-single": {
      // Spells are front-weighted (0.2 back chance) unless the metadata flips it.
      const backChance = profile.spellPrefersBack === true
        ? 1 - SPELL_OFF_ROW_CHANCE
        : SPELL_OFF_ROW_CHANCE;
      return [pickWeightedRow(alive, backChance, rng).instanceId];
    }
  }
}

/**
 * Derive an `EnemyAttackProfile` from a `MonsterEntity` (EV-E1). Enemies
 * with an authored ranged attack use the spec's ranged-single row
 * weighting (80/20 front/back); everything else remains `melee-reach-1`.
 * AoE and spell shapes arrive with enemy casting (EV-E3).
 */
export function getEnemyAttackProfile(entity: MonsterEntity): EnemyAttackProfile {
  if (entity.attack?.ranged) return { shape: "ranged-single" };
  return { shape: "melee-reach-1" };
}

// ── Helpers ──────────────────────────────────────────────────────────────────

function frontRow(members: readonly TargetingMember[]): TargetingMember[] {
  return members.filter((m) => m.formationSlot?.row === 0);
}

function backRow(members: readonly TargetingMember[]): TargetingMember[] {
  return members.filter((m) => m.formationSlot?.row === 1);
}

function hasAnySlot(members: readonly TargetingMember[]): boolean {
  return members.some((m) => m.formationSlot !== null);
}

function clamp01(n: number): number {
  return Math.max(0, Math.min(1, n));
}

function pickUniform<T>(pool: readonly T[], rng: Rng): T {
  return pool[rng(0, pool.length - 1)]!;
}

/**
 * Roll a back-vs-front coin at `backChance`, then uniform-pick within that row.
 * Falls back to uniform across all alive members when nobody has a formation
 * slot assigned (v1 MVP case: single-character party).
 */
function pickWeightedRow(
  alive: readonly TargetingMember[],
  backChance: number,
  rng: Rng,
): TargetingMember {
  if (!hasAnySlot(alive)) return pickUniform(alive, rng);

  const front = frontRow(alive);
  const back = backRow(alive);
  // 0..99 integer bucket keeps the rng contract uniform with randomInt.
  const thresholdPct = Math.round(backChance * 100);
  const roll = rng(0, 99);
  const wantBack = roll < thresholdPct;

  if (wantBack && back.length > 0) return pickUniform(back, rng);
  if (!wantBack && front.length > 0) return pickUniform(front, rng);
  // Preferred row empty — fall through to whichever row has live targets.
  if (front.length > 0) return pickUniform(front, rng);
  return pickUniform(back, rng);
}
